import { LeaderboardPlayerData } from "../../../models/leaderboard";
import { MatchSetupConfig } from "../../../models/match";
import { formatDateToDDMMMYYYY } from "../../../utils/dateUtils";
import {
  combinePlayerScores,
  LeaderboardPlayerDataStrings,
} from "./utils/leaderboardCalculations";

interface PlayerLeaderboardTableProps {
  data: LeaderboardPlayerData[];
  matchSetups: MatchSetupConfig[];
}

const PlayerCard = ({
  player,
  matchSetups,
  index,
}: {
  player: LeaderboardPlayerDataStrings;
  matchSetups: MatchSetupConfig[];
  index: number;
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center">
        {player.profilePicture && (
          <img
            src={`/api/static/images/${player.profilePicture}`}
            alt={`${player.firstName} ${player.lastName}`}
            className="w-10 h-10 rounded-full mr-3"
          />
        )}
        <div>
          <div className="font-medium text-gray-900 dark:text-white">
            {player.firstName} {player.lastName}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Rank: {index + 1}
          </div>
        </div>
      </div>
      <div className="text-lg font-bold text-gray-900 dark:text-white">
        {(player.rankingScore ?? 0).toFixed(2)}
      </div>
    </div>
    <div className="space-y-2">
      {matchSetups.map((match) => (
        <div
          key={match.matchId}
          className="flex justify-between text-sm border-t dark:border-gray-700 pt-2"
        >
          <span className="text-gray-600 dark:text-gray-400">
            {match.courseName} -{formatDateToDDMMMYYYY(match.date ?? "")}
          </span>
          <span className="font-medium text-gray-900 dark:text-white">
            {player.matchScores?.[match.matchId] ?? " - "}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const PlayerDataRow = ({
  player,
  index,
  matchSetups,
}: {
  player: LeaderboardPlayerDataStrings;
  index: number;
  matchSetups: MatchSetupConfig[];
}) => {
  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
      <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
        {index + 1}
      </td>
      <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
        <div className="flex items-center">
          {player.profilePicture && (
            <img
              src={`/api/static/images/${player.profilePicture}`}
              alt={`${player.firstName} {player.lastName}`}
              className="w-8 h-8 rounded-full mr-2"
            />
          )}
          <span>
            {player.firstName} {player.lastName}
          </span>
        </div>
      </td>
      {matchSetups
        .filter((m) => m.groupOrKnockout === "Group")
        .map((match) => (
          <td
            key={match.matchId}
            className="px-4 py-2 whitespace-nowrap text-center text-gray-900 dark:text-gray-100"
          >
            {player.matchScores?.[match.matchId] ?? " - "}
          </td>
        ))}
      <td className="px-4 py-2 whitespace-nowrap text-center font-bold text-gray-900 dark:text-gray-100">
        {(player.rankingScore ?? 0).toFixed(2)}
      </td>
    </tr>
  );
};

const downloadTableAsCSV = (
  players: LeaderboardPlayerDataStrings[],
  matchSetups: MatchSetupConfig[]
) => {
  const rows: string[] = [];

  // Header row
  const headers = [
    "Rank",
    "Player",
    ...matchSetups
      .filter((m) => m.groupOrKnockout === "Group")
      .map(
        (match) =>
          `${match.courseName} - ${formatDateToDDMMMYYYY(match.date ?? "")}`
      ),
    "Total",
  ];
  rows.push(headers.join(","));

  // Player data rows
  players.forEach((player, index) => {
    const row = [
      index + 1,
      `${player.firstName} ${player.lastName}`,
      ...matchSetups
        .filter((m) => m.groupOrKnockout === "Group")
        .map((match) => player.matchScores?.[match.matchId] ?? "-"),
      (player.rankingScore ?? 0).toFixed(2),
    ];
    rows.push(row.join(","));
  });

  // Create and trigger download
  const csvContent = "data:text/csv;charset=utf-8," + rows.join("\n");
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", `HPGL-V-MVP-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const PlayerLeaderboardTable = ({
  data,
  matchSetups,
}: PlayerLeaderboardTableProps) => {
  const playerData = combinePlayerScores(data).sort(
    (a, b) => (b.rankingScore ?? 0) - (a.rankingScore ?? 0)
  );

  return (
    <>
      {/* Mobile View (Cards) */}
      <div className="md:hidden">
        {playerData.map((player, index) => (
          <PlayerCard
            key={player.id}
            player={player}
            matchSetups={matchSetups}
            index={index}
          />
        ))}
      </div>

      {/* Desktop View (Table) */}
      <div className="hidden md:block overflow-x-auto">
        <div className="relative">
          <div className="flex justify-end mb-4">
            <button
              onClick={() => downloadTableAsCSV(playerData, matchSetups)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span>Download CSV</span>
            </button>
          </div>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-4 py-2 text-gray-900 dark:text-gray-100">
                  Rank
                </th>
                <th className="px-4 py-2 text-gray-900 dark:text-gray-100">
                  Player
                </th>
                {matchSetups
                  .filter((m) => m.groupOrKnockout === "Group")
                  .map((match) => (
                    <th
                      key={match.matchId}
                      className="px-4 py-2 text-center text-gray-900 dark:text-gray-100"
                    >
                      {match.courseName} -
                      {formatDateToDDMMMYYYY(match.date ?? "")}
                    </th>
                  ))}
                <th className="px-4 py-2 text-center text-gray-900 dark:text-gray-100">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {playerData.map((player, index) => (
                <PlayerDataRow
                  key={player.id}
                  player={player}
                  index={index}
                  matchSetups={matchSetups}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
